import React, { createContext, useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, getDocs, doc, updateDoc, increment, arrayUnion } from "firebase/firestore"; 

export const BlogContext = createContext();

export const BlogProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const postsCollection = collection(db, 'blogs');
        const postSnapshot = await getDocs(postsCollection);
        const postList = postSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPosts(postList);
      } catch (err) {
        console.error("Error fetching posts from Firestore:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Get only public posts for the blog page
  const getPublicPosts = () => {
    return posts.filter(post => post.visibility !== 'private');
  };

  // Get a specific post by ID (for direct access to unlisted posts)
  const getPostById = (id) => {
    const post = posts.find(post => post.id === id);
    // Return post if it's public or unlisted, but not if it's private
    return post && post.visibility !== 'private' ? post : null;
  };

  const trackView = async (postId) => {
    try {
      // Get user's IP address and location info
      const getClientInfo = async () => {
        try {
          // Get IP and location data
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();

          // Get location info (you might want to use a more robust geolocation service)
          const locationResponse = await fetch(`http://ip-api.com/json/${ipData.ip}`);
          const locationData = await locationResponse.json();

          return {
            ip: ipData.ip,
            location: {
              country: locationData.country || 'Unknown',
              city: locationData.city || 'Unknown',
              region: locationData.regionName || 'Unknown'
            }
          };
        } catch {
          // Fallback to a combination of user agent and timestamp for local development
          return {
            ip: btoa(navigator.userAgent + new Date().toDateString()).slice(0, 16),
            location: {
              country: 'Local Development',
              city: 'Local',
              region: 'Local'
            }
          };
        }
      };

      const clientInfo = await getClientInfo();
      const now = new Date();

      // Get device and browser info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'Mobile' :
                    /Tablet|iPad/.test(navigator.userAgent) ? 'Tablet' : 'Desktop',
        browser: getBrowserInfo(navigator.userAgent),
        os: getOSInfo(navigator.userAgent),
        // eslint-disable-next-line no-restricted-globals
        screenResolution: `${screen.width}x${screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const viewKey = `view_${postId}_${clientInfo.ip}`;

      // Check if this IP has viewed this post recently
      const lastViewTime = localStorage.getItem(viewKey);

      if (lastViewTime) {
        const timeDiff = now.getTime() - parseInt(lastViewTime);
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

        // If less than an hour has passed, don't count as a new view
        if (timeDiff < oneHour) {
          return;
        }
      }

      // Set a timeout to track the view after 15 seconds
      setTimeout(async () => {
        try {
          // Check if user is still on the page
          if (document.visibilityState === 'visible') {
            const postRef = doc(db, 'blogs', postId);

            // Create analytics entry
            const analyticsEntry = {
              timestamp: now.toISOString(),
              ip: clientInfo.ip,
              location: clientInfo.location,
              deviceInfo,
              referrer: document.referrer || 'Direct',
              sessionDuration: 0, // Will be updated when user leaves
              userId: localStorage.getItem('user_session') || generateSessionId()
            };

            // Update the post with view count and analytics
            await updateDoc(postRef, {
              views: increment(1),
              lastViewed: new Date().toISOString(),
              // Add analytics data to an array field
              analytics: arrayUnion(analyticsEntry)
            });

            // Store the view time in localStorage
            localStorage.setItem(viewKey, now.getTime().toString());

            // Generate a session ID if not exists
            if (!localStorage.getItem('user_session')) {
              localStorage.setItem('user_session', generateSessionId());
            }
          }
        } catch (error) {
          console.error('Error tracking delayed view:', error);
        }
      }, 15000); // 15 seconds

    } catch (error) {
      console.error('Error setting up view tracking:', error);
    }
  };

  // Helper function to get browser info
  const getBrowserInfo = (userAgent) => {
    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    return 'Other';
  };

  // Helper function to get OS info
  const getOSInfo = (userAgent) => {
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Other';
  };

  // Helper function to generate session ID
  const generateSessionId = () => {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  return (
    <BlogContext.Provider value={{ posts, loading, trackView, getPublicPosts, getPostById }}>
      {children}
    </BlogContext.Provider>
  );
};